// Print Preview overlay for Decision Brief (DMD-1100 / DMD-1103)
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Printer } from 'lucide-react';

export interface BriefPrintMetric {
  label: string;
  value: string;
}

export interface BriefPrintSection {
  title: string;
  /** Markdown or plain text. Empty → section omitted. */
  body: string;
}

export interface BriefPrintListItem {
  title: string;
  meta?: string;
  detail?: string;
}

export interface BriefPrintSnapshot {
  title: string;
  category: string;
  recommendation: string;
  /** True when the verdict came from client-side score thresholds, not the API (DMD-1160). */
  recommendationIsDerived?: boolean;
  metrics: BriefPrintMetric[];
  scoreFreshnessLabel?: string | null;
  scoreIsLive?: boolean;
  /** Ordered profile sections reflecting Brief content at export time. */
  sections: BriefPrintSection[];
  risks?: BriefPrintListItem[];
  milestones?: BriefPrintListItem[];
  requirements?: BriefPrintListItem[];
}

export interface BriefPrintPreviewProps {
  open: boolean;
  onClose: () => void;
  snapshot: BriefPrintSnapshot;
  generatedAt?: Date;
}

const MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 text-sm leading-relaxed text-[#4b5563] last:mb-0">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 text-sm text-[#4b5563]">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 text-sm text-[#4b5563]">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-[#18365E]">{children}</strong>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-1.5 text-sm font-bold text-[#18365E]">{children}</h3>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-1.5 text-sm font-bold text-[#18365E]">{children}</h3>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-[#18365E]">{children}</h4>
  ),
};

/** Rough content weight used to pack blocks onto letter-sized preview pages. */
const PAGE_BUDGET = 42;

function PrintBody({ body }: { body: string }) {
  const s = body.trim();
  if (!s) return null;
  return (
    <div className="max-w-none">
      <ReactMarkdown components={MD_COMPONENTS}>{s}</ReactMarkdown>
    </div>
  );
}

function PrintList({
  title,
  items,
}: {
  title: string;
  items: BriefPrintListItem[];
}) {
  if (!items.length) return null;
  return (
    <section className="brief-print-section relative mb-5 break-inside-avoid">
      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#18365E]">{title}</h2>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li
            key={`${item.title}-${i}`}
            className="rounded-lg border border-[#e5e7eb] bg-[#F5F7FA] px-3 py-2 break-inside-avoid"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-[#18365E]">{item.title}</p>
              {item.meta ? (
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#717182]">
                  {item.meta}
                </span>
              ) : null}
            </div>
            {item.detail ? (
              <p className="mt-1 text-xs leading-relaxed text-[#4b5563] whitespace-pre-wrap">{item.detail}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProductWatermark() {
  return (
    <div
      className="brief-print-watermark pointer-events-none absolute inset-0 z-20 flex items-center justify-center overflow-hidden"
      aria-hidden
    >
      <span
        className="brief-print-watermark-text select-none text-[64px] font-black uppercase tracking-[0.18em] rotate-[-28deg] sm:text-[72px]"
        style={{ color: 'rgba(24, 54, 94, 0.18)' }}
      >
        Adaptive
      </span>
    </div>
  );
}

function PrintPage({
  children,
  pageNumber,
  totalPages,
  dateLabel,
}: {
  children: React.ReactNode;
  pageNumber: number;
  totalPages: number;
  dateLabel: string;
}) {
  return (
    <article
      className="brief-print-page relative mx-auto flex min-h-[960px] w-full max-w-[720px] flex-col overflow-hidden rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm print:min-h-0 print:max-w-none print:rounded-none print:border-0 print:shadow-none print:p-0"
      data-page={pageNumber}
    >
      <ProductWatermark />
      <div className="relative z-[1] flex min-h-0 flex-1 flex-col">{children}</div>
      <div className="brief-print-meta relative z-[1] mt-auto flex items-center justify-between border-t border-[#e5e7eb] pt-3 text-[10px] text-[#717182]">
        <span>Adaptive · Data Value Accelerator · Generated {dateLabel} · Status: PREVIEW</span>
        <span className="font-semibold text-[#18365E]">
          Page {pageNumber} of {totalPages}
        </span>
      </div>
    </article>
  );
}

function estimateWeight(block: { kind: string; body?: string; items?: BriefPrintListItem[] }): number {
  if (block.kind === 'cover') return 18;
  if (block.kind === 'section') {
    const len = String(block.body ?? '').length;
    return Math.min(28, Math.max(8, Math.ceil(len / 120)));
  }
  if (block.kind === 'list') {
    return Math.min(24, Math.max(8, 4 + (block.items?.length ?? 0) * 3));
  }
  return 8;
}

function packPages<T extends { weight: number }>(blocks: T[]): T[][] {
  if (!blocks.length) return [[]];
  const pages: T[][] = [];
  let current: T[] = [];
  let used = 0;

  for (const block of blocks) {
    const next = used + block.weight;
    if (current.length > 0 && next > PAGE_BUDGET) {
      pages.push(current);
      current = [block];
      used = block.weight;
    } else {
      current.push(block);
      used = next;
    }
  }
  if (current.length) pages.push(current);
  return pages;
}

function sanitizePrintFileName(title: string): string {
  const cleaned = String(title ?? '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || 'Use Case Profile';
}

export const BriefPrintPreview: React.FC<BriefPrintPreviewProps> = ({
  open,
  onClose,
  snapshot,
  generatedAt,
}) => {
  /** Frozen when preview opens so metadata stays accurate through print (DMD-1103). */
  const frozenGeneratedAt = React.useMemo(() => {
    if (!open) return null;
    return generatedAt ?? new Date();
    // Intentionally only re-freeze when the preview is (re)opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const previousTitleRef = React.useRef<string | null>(null);
  const printFileName = sanitizePrintFileName(snapshot.title);

  const applyPrintFileName = React.useCallback(() => {
    if (previousTitleRef.current == null) {
      previousTitleRef.current = document.title;
    }
    // Browser "Save as PDF" uses document.title as the default filename (DMD-1100).
    document.title = printFileName;
  }, [printFileName]);

  const restoreDocumentTitle = React.useCallback(() => {
    if (previousTitleRef.current == null) return;
    document.title = previousTitleRef.current;
    previousTitleRef.current = null;
  }, []);

  const handlePrint = React.useCallback(() => {
    applyPrintFileName();
    // Give the browser a tick to commit the title before the print dialog snapshots it.
    window.setTimeout(() => {
      window.print();
    }, 0);
  }, [applyPrintFileName]);

  React.useEffect(() => {
    if (!open) return;
    const cls = 'brief-print-active';
    document.body.classList.add(cls);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    applyPrintFileName();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    // Re-assert use-case title after print in case the dialog/session reset it.
    const onAfterPrint = () => applyPrintFileName();
    document.addEventListener('keydown', onKey);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      document.body.classList.remove(cls);
      document.body.style.overflow = previousOverflow;
      restoreDocumentTitle();
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [open, applyPrintFileName, restoreDocumentTitle]);

  const pages = React.useMemo(() => {
    type Block =
      | { kind: 'cover'; weight: number }
      | { kind: 'section'; weight: number; title: string; body: string }
      | { kind: 'list'; weight: number; title: string; items: BriefPrintListItem[] };

    const sections = snapshot.sections.filter((s) => String(s.body ?? '').trim().length > 0);
    const blocks: Block[] = [
      { kind: 'cover', weight: estimateWeight({ kind: 'cover' }) },
      ...sections.map((s) => ({
        kind: 'section' as const,
        title: s.title,
        body: s.body,
        weight: estimateWeight({ kind: 'section', body: s.body }),
      })),
    ];

    const lists: { title: string; items: BriefPrintListItem[] }[] = [
      { title: 'Risk Register', items: snapshot.risks ?? [] },
      { title: 'Implementation Roadmap', items: snapshot.milestones ?? [] },
      { title: 'Implementation Requirements', items: snapshot.requirements ?? [] },
    ].filter((l) => l.items.length > 0);

    for (const list of lists) {
      blocks.push({
        kind: 'list',
        title: list.title,
        items: list.items,
        weight: estimateWeight({ kind: 'list', items: list.items }),
      });
    }

    return packPages(blocks);
  }, [snapshot]);

  if (!open || !frozenGeneratedAt) return null;

  const dateLabel = frozenGeneratedAt.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const totalPages = Math.max(1, pages.length);

  return (
    <div
      className="brief-print-root fixed inset-0 z-[80] flex flex-col bg-[#F5F7FA] print:static print:z-auto print:bg-white"
      data-brief-print="true"
      role="dialog"
      aria-modal="true"
      aria-label="Print preview"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-5 py-3 print:hidden">
        <div>
          <p className="text-sm font-bold text-[#18365E]">Print Preview</p>
          <p className="text-[11px] text-[#717182]">
            {totalPages} page{totalPages === 1 ? '' : 's'} · Adaptive watermark on each page · Review before exporting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-1.5 rounded-lg bg-[#18365E] px-3 py-2 text-xs font-semibold text-white hover:bg-[#122a4a]"
          >
            <Printer className="h-3.5 w-3.5" />
            Print / Save as PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#e5e7eb] p-2 text-[#717182] hover:border-[#18365E] hover:text-[#18365E]"
            aria-label="Close print preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="brief-print-pages relative min-h-0 flex-1 space-y-6 overflow-auto p-6 print:space-y-0 print:overflow-visible print:p-0">
        {pages.map((pageBlocks, pageIndex) => {
          const pageNumber = pageIndex + 1;
          return (
            <PrintPage
              key={`page-${pageNumber}`}
              pageNumber={pageNumber}
              totalPages={totalPages}
              dateLabel={dateLabel}
            >
              {pageBlocks.map((block, blockIndex) => {
                if (block.kind === 'cover') {
                  return (
                    <div key={`cover-${blockIndex}`} className="mb-5">
                      <header className="mb-6 border-b border-[#e5e7eb] pb-4 break-inside-avoid">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF6E4C]">
                              Adaptive · Data Value Accelerator
                            </p>
                            <h1 className="mt-1 text-xl font-bold leading-tight text-[#18365E]">
                              {snapshot.title}
                            </h1>
                            {snapshot.category ? (
                              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#717182]">
                                {snapshot.category}
                              </p>
                            ) : null}
                          </div>
                          <div className="shrink-0 rounded-md border border-[#e5e7eb] bg-[#F5F7FA] px-3 py-2 text-right text-[11px] text-[#4b5563]">
                            <p>
                              <span className="font-semibold text-[#18365E]">Status:</span> PREVIEW
                            </p>
                            <p>
                              <span className="font-semibold text-[#18365E]">Generated:</span> {dateLabel}
                            </p>
                            <p className="mt-1.5 inline-flex rounded border border-[#18365E]/30 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#18365E]">
                              {snapshot.recommendation}
                            </p>
                            {snapshot.recommendationIsDerived ? (
                              <p className="mt-1 text-[9px] italic text-[#717182]">
                                Derived from composite score
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </header>

                      <section className="break-inside-avoid">
                        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#18365E]">
                          Key metrics
                        </h2>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {snapshot.metrics.map((m) => (
                            <div
                              key={m.label}
                              className="rounded-lg border border-[#e5e7eb] bg-[#F5F7FA] px-3 py-2"
                            >
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">
                                {m.label}
                              </p>
                              <p className="text-sm font-bold text-[#18365E]">{m.value}</p>
                            </div>
                          ))}
                        </div>
                        {snapshot.scoreFreshnessLabel ? (
                          <p className="mt-2 text-[11px] text-[#717182]">
                            Score status:{' '}
                            <span className="font-semibold text-[#18365E]">
                              {snapshot.scoreIsLive ? 'Live' : 'Prior computation'}
                            </span>
                            {' · '}
                            {snapshot.scoreFreshnessLabel}
                          </p>
                        ) : null}
                      </section>
                    </div>
                  );
                }

                if (block.kind === 'section') {
                  return (
                    <section
                      key={`${block.title}-${blockIndex}`}
                      className="brief-print-section relative mb-5"
                    >
                      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#18365E]">
                        {block.title}
                      </h2>
                      <PrintBody body={block.body} />
                    </section>
                  );
                }

                return (
                  <PrintList
                    key={`${block.title}-${blockIndex}`}
                    title={block.title}
                    items={block.items}
                  />
                );
              })}
            </PrintPage>
          );
        })}
      </div>
    </div>
  );
};

export default BriefPrintPreview;
